import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import apiFetch from '@wordpress/api-fetch'
import { ReactContextError } from '../utils/reactUtils'
import {
    JoueurType,
    RankedRow,
    Row,
    RowDetailsEntry,
    SortBy,
} from '../competitions/StatsJoueurs/data'
import { useMediaQuery } from '../hooks'
import { normalize, removeExtraSpaces } from '../utils/stringUtils'
import { xorDecipherAndParse } from '../shared/utils'

type StatsJoueursDataContextValue = {
    rows: RankedRow[]
    /**
     * 1-based
     */
    page: number
    setPage: (v: number) => void
    joueurKeyword: string
    setJoueurKeyword: (v: string) => void
    sortBy: SortBy
    setSortBy: (v: SortBy) => void
    joueurType: JoueurType
    setJoueurType: (v: JoueurType) => void
    statsJoueursDetails: { [key: string]: RowDetailsEntry[] | null } // null = error
    /**
     * fetching details for individuId => individuId, else null
     */
    fetchingDetails: string | null
    fetchDetails: (individuId: string) => Promise<RowDetailsEntry[]>
    isDesktop: boolean
}

// @ts-ignore
const StatsJoueursDataContext = createContext<StatsJoueursDataContextValue>(null)

const SORT_BY_STRATEGIES: {
    [x in SortBy]: (left: Row, right: Row, context: { joueurType: JoueurType }) => number
} = {
    nom: (a, b) =>
        // case insensitive
        a.nom.localeCompare(b.prenom, 'fr', { sensitivity: 'base' }) ||
        a.prenom.localeCompare(b.nom, 'fr', { sensitivity: 'base' }),
    club: (a, b) => a.equipeLibelle.localeCompare(b.equipeLibelle, 'fr', { sensitivity: 'base' }), // case insensitive
    buts_ou_arrets: (a, b, { joueurType }) => {
        switch (joueurType) {
            case 'joueur_de_champ': {
                return +b.totalButs - +a.totalButs
            }
            case 'gardien': {
                return +b.totalArrets - +a.totalArrets
            }
            default: {
                return 0
            }
        }
    },
}

const FILTER_JOUEUR_TYPE_STRATEGIES: { [x in JoueurType]: (x: Row) => boolean } = {
    joueur_de_champ: () => true,
    gardien: (x) => +x.totalArrets > 0,
}

const useStatsJoueursDetails = ({
    extPouleId,
    extEquipeId,
}: {
    extPouleId?: string
    extEquipeId?: string
}) => {
    const [statsJoueursDetails, setStatsJoueursDetails] = useState<{
        [key: string]: RowDetailsEntry[] | null // null = error
    }>({})

    const [fetchingDetails, setFetchingDetails] = useState<string | null>(null)

    const fetchDetails: (individuId: string) => Promise<RowDetailsEntry[]> = useCallback(
        async (individuId: string) => {
            const cachedValue = statsJoueursDetails[individuId]

            if (cachedValue) {
                return cachedValue
            }

            setFetchingDetails(individuId)

            try {
                const data = await apiFetch<string>({
                    path: `competitions/v1/stats-joueurs/?individuId=${individuId}${
                        extPouleId ? `&extPouleId=${extPouleId}` : `&extEquipeId=${extEquipeId}`
                    }`,
                })
                const jsonData = xorDecipherAndParse(data) as RowDetailsEntry[]

                setStatsJoueursDetails((x) => ({ ...x, [individuId]: jsonData }))
                return jsonData
            } catch (e) {
                setStatsJoueursDetails((x) => ({ ...x, [individuId]: null }))
                return []
            } finally {
                setFetchingDetails(null)
            }
        },
        [extEquipeId, extPouleId, statsJoueursDetails],
    )

    return { statsJoueursDetails, fetchingDetails, fetchDetails }
}

const FILTER_BY_NAME_PREDICATE = <T extends { prenom: string; nom: string }>(
    x: T,
    keyword: string,
): boolean => {
    const processedKeyword = normalize(removeExtraSpaces(keyword)).toLowerCase()
    if (!processedKeyword) return true

    return (
        normalize(`${x.prenom} ${x.nom}`).toLowerCase().includes(processedKeyword) ||
        normalize(`${x.nom} ${x.prenom}`).toLowerCase().includes(processedKeyword)
    )
}

const useStatsJoueursData = ({
    extPouleId,
    extEquipeId,
    rowsData,
}: {
    rowsData: Row[]
    extPouleId: string
    extEquipeId: string
}): StatsJoueursDataContextValue => {
    // todo: see if reset page, keyword etc when rowsData changes (not needed if rowsData changes only if there is a whole page reload)
    const [page, setPage] = useState(1)
    const [joueurKeyword, setJoueurKeyword] = useState('')
    const [sortBy, setSortBy] = useState<SortBy>('buts_ou_arrets')
    const [joueurType, setJoueurType] = useState<JoueurType>('joueur_de_champ')

    const rows: RankedRow[] = useMemo(
        () =>
            rowsData
                .filter(FILTER_JOUEUR_TYPE_STRATEGIES[joueurType])
                .filter((x) => FILTER_BY_NAME_PREDICATE(x, joueurKeyword))
                .sort((a, b) => SORT_BY_STRATEGIES[sortBy](a, b, { joueurType }))
                .map((x, i) => ({ ...x, rank: i + 1 })),
        [rowsData, joueurType, joueurKeyword, sortBy],
    )

    const { statsJoueursDetails, fetchingDetails, fetchDetails } = useStatsJoueursDetails({
        extPouleId,
        extEquipeId,
    })

    const isDesktop = !useMediaQuery('(max-width: 996px)')

    return {
        rows,
        page,
        setPage,
        joueurKeyword,
        setJoueurKeyword,
        sortBy,
        setSortBy,
        joueurType,
        setJoueurType,
        statsJoueursDetails,
        fetchingDetails,
        fetchDetails,
        isDesktop,
    }
}

type StatsJoueursDataProviderProps = {
    children: React.ReactNode
    rowsData: Row[]
    extPouleId: string
    extEquipeId: string
}

export const StatsJoueursDataProvider = ({
    children,
    extPouleId,
    extEquipeId,
    rowsData,
}: StatsJoueursDataProviderProps) => (
    <StatsJoueursDataContext.Provider
        value={useStatsJoueursData({ extPouleId, extEquipeId, rowsData })}
    >
        {children}
    </StatsJoueursDataContext.Provider>
)

export const useStatsJoueursDataContext = () => {
    const context = useContext(StatsJoueursDataContext)
    if (context === null) {
        throw new ReactContextError('StatsJoueursDataProvider')
    }
    return context
}
